import {
  Badge,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import { alpha, Box, styled } from '@mui/system';
import Grid from "@mui/material/Grid"
import NotificationDrawer from 'src/layouts/SidebarLayout/NotificationDrawer';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import { handleGetNotification } from 'src/utils/sendNotification';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import { setCurrentPermitDetails } from 'src/redux-store/slices/currentPermitSlice';
import { buildPermitApplicationPath, getDepartmentNameByPermitId } from 'src/utils/permitDepartment';
import { findPermitAssignment } from 'src/utils/permitFeatureAccess';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

function HeaderNotifications() {
  const ref = useRef<any>(null);
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const list = useSelector((state: any) => state.notifications.notifications)
  const userDetails = useSelector((state: RootState) => state.user.userDetails)
  const [open, setOpen] = useState<boolean>(false)
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] = useState<{ [key: string]: string }>()

  useEffect(() => {
    handleGetNotification(dispatch)
  }, [])

  const handleOpen = (): void => {
    setShowNotifications(true);
    handleGetNotification(dispatch)
  };

  const handleReadNotification = async (data, action) => {
    if (data?.Read === false) {
      const response = await httpRequest(
        'PATCH',
        `/notification/updateStatus/${data?.ID}`,
      );
      if (response?.status === "success") {
        handleGetNotification(dispatch)
        if (action === "Redirect") {
          setSelectedNotification({ ...data })
          setShowNotifications(false)
          setOpen(true)
        }
      } else {
        toast?.error(response?.message)
      }
    } else {
      if (action === "Redirect") {
        setSelectedNotification({ ...data })
        setShowNotifications(false)
        setOpen(true)
      }
    }
  }

  const handleNotificationRedirection = () => {
    if (!selectedNotification) {
      return;
    }

    const { TownshipID, PermitID, ApplicationID } = selectedNotification;
    const permitId = Number(PermitID);
    const departmentName = getDepartmentNameByPermitId(permitId);
    const targetPath = buildPermitApplicationPath({
      townshipId: TownshipID,
      permitId,
      applicationId: ApplicationID,
      view: ApplicationID ? 'view' : 'list'
    });

    if (!targetPath || !departmentName) {
      toast.error('Unable to open this application.');
      return;
    }

    const assignment = findPermitAssignment(userDetails, permitId);
    dispatch(
      setCurrentPermitDetails({
        id: permitId,
        townshipId: Number(TownshipID),
        departmentName,
        currentPermitUserRole: assignment?.permitRole ?? ''
      })
    );

    setOpen(false);
    navigate(targetPath);
  }

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={list?.length && list?.filter((data) => data?.Read === false)?.length}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      <NotificationDrawer
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        handleReadNotification={handleReadNotification}
      />
      <CustomModal
        title={
          <Box display={"flex"} alignItems={"center"}>
            <NotificationsActiveTwoToneIcon />
            <Typography sx={{
              pl: 2,
              pr: 5,
              fontWeight: 600,
              fontSize: '18px',
              minWidth: '400px'
            }}>
              {selectedNotification?.Title}
            </Typography>
          </Box>
        }
        IsOpen={open}
        setIsOpen={setOpen}
        onSave={handleNotificationRedirection}
        actionButtons={['Close', 'Go To Application']}
        loading={false}
        content={
          <Grid item xs={12}>
            <Grid item xs={6}>
              <Typography fontSize={"14px"} fontWeight={"bold"} mb={1}>
                {selectedNotification?.ApplicationType}
              </Typography>
              <Typography fontSize={"12px"}>
              </Typography>
            </Grid>
            <Typography>
              {selectedNotification?.Message}
            </Typography>
          </Grid>
        }
      />
    </>
  );
}

export default HeaderNotifications;
